import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BaseButton from "@reusables/BaseButton";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { CustomerGroupsFormTyping, customerGroupsScheme } from "./types";
import BaseInput from "@reusables/BaseInput";
import { ReactComponent as PercentageIcon } from "@assets/icons/ic_percentage.svg";

import BaseInputsGrid from "@reusables/BaseInputsGrid";
import CustomerSection from "./parts/CustomerSection";
import ProductSection from "./parts/ProductSection";
import { CustomerGroups } from "@/types/general";

import { cn } from "@helpers/utils";

interface MutationLayoutProperties {
    customerGroup: CustomerGroups.Extended | undefined;
    onSubmit: (args: CustomerGroupsFormTyping) => void;
}

export default function MutationLayout(props: MutationLayoutProperties) {
    const { t } = useTranslation("", { keyPrefix: "customerGroups.mutation" });

    const form = useForm<CustomerGroupsFormTyping>({
        resolver: zodResolver(customerGroupsScheme),
        defaultValues: {
            customers: [],
            products: [],
        },
    });

    useEffect(() => {
        const { setValue } = form;
        if (props.customerGroup) {
            setValue("code", props.customerGroup.code);
            setValue("name", props.customerGroup.name);
            setValue("discount", props.customerGroup.discount);
            setValue(
                "customers",
                props.customerGroup.customers.map((customer) => ({ value: customer }))
            );
            setValue("products", props.customerGroup.products);
        }
    }, [props.customerGroup]);

    useEffect(() => {
        form.setFocus("code");
    }, []);

    const onSubmit = form.handleSubmit((data: CustomerGroupsFormTyping) => {
        props.onSubmit(data);
    });

    return (
        <>
            <FormProvider {...form}>
                <BaseInputsGrid>
                    <Controller
                        control={form.control}
                        name="code"
                        render={({ field, fieldState }) => (
                            <div className="relative">
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("fields.groupCode.label")}
                                    placeholder={t("fields.groupCode.placeholder")}
                                    disabled={!!props.customerGroup}
                                />
                            </div>
                        )}
                    />

                    <Controller
                        control={form.control}
                        name="name"
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.groupName.label") + " *"}
                                placeholder={t("fields.groupName.placeholder")}
                            />
                        )}
                    />

                    <Controller
                        control={form.control}
                        name="discount"
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.discount.label") + " *"}
                                placeholder={t("fields.discount.placeholder")}
                                icon={{ right: { el: <PercentageIcon />, offset: "18px" } }}
                                sx={{
                                    "& input": {
                                        paddingRight: "40px",
                                    },
                                }}
                            />
                        )}
                    />
                </BaseInputsGrid>

                <CustomerSection />

                <ProductSection />

                <StickySaveFooter saveText={t("buttons.save")} onSave={() => onSubmit()} />
            </FormProvider>
        </>
    );
}

interface StickySaveFooterProps {
    saveText: string;
    onSave: () => void;
}

function StickySaveFooter({ saveText, onSave }: StickySaveFooterProps) {
    const footerRef = useRef<HTMLDivElement>(null);
    const footerSentinelRef = useRef<HTMLDivElement>(null);
    const [isDocked, setIsDocked] = useState(false);

    useLayoutEffect(() => {
        const footer = footerRef.current;
        const footerSentinel = footerSentinelRef.current;

        if (!footer || !footerSentinel) {
            return;
        }

        let frameId = 0;

        const updateDockedState = () => {
            frameId = 0;

            const viewportBottom = window.innerHeight;
            const footerRectangle = footer.getBoundingClientRect();
            const footerSentinelRectangle = footerSentinel.getBoundingClientRect();

            const isFooterVisible =
                footerRectangle.top < viewportBottom && footerRectangle.bottom > 0;
            const isTouchingViewportBottom = footerRectangle.bottom >= viewportBottom - 1;
            const isFooterStillDocked = footerSentinelRectangle.top > viewportBottom + 1;

            setIsDocked(
                isFooterVisible && isTouchingViewportBottom && isFooterStillDocked
            );
        };

        const scheduleDockedStateUpdate = () => {
            if (!frameId) {
                frameId = window.requestAnimationFrame(updateDockedState);
            }
        };

        scheduleDockedStateUpdate();

        window.addEventListener("scroll", scheduleDockedStateUpdate, { passive: true });
        window.addEventListener("resize", scheduleDockedStateUpdate);

        return () => {
            if (frameId) {
                window.cancelAnimationFrame(frameId);
            }

            window.removeEventListener("scroll", scheduleDockedStateUpdate);
            window.removeEventListener("resize", scheduleDockedStateUpdate);
        };
    }, []);

    return (
        <>
            <div
                ref={footerRef}
                className={cn(
                    "sticky bottom-0 z-20 flex justify-center mt-[32px] -mx-[24px] border-t border-solid border-transparent px-[24px] py-[16px] transition-[border-color,background-color,box-shadow] duration-150 ease-out motion-reduce:transition-none",
                    isDocked
                        ? "border-[#F3F2EF] bg-card shadow-[0_-8px_18px_rgba(59,65,208,0.04)]"
                        : "bg-transparent shadow-none"
                )}
            >
                <BaseButton text={saveText} size="md" buttonWidth="200px" onClick={onSave} />
            </div>

            <div ref={footerSentinelRef} className="h-px" aria-hidden="true" />
        </>
    );
}
